import { TOP_BAR_CONTENT_TYPES, TOP_BAR_LAYOUT } from '../constants';
import { type TopBarContent, type TopBarLayout } from '../types';

type ResolveAlertBannerParams = {
    isCompanyNameModerationBannerOpen: boolean;
    isCompanyNameModerationLoading: boolean;
    isStoreBannedBannerOpen: boolean;
};

type ResolvePromoBannerParams = {
    isCallMeBannerOpen: boolean;
    isConfiguredBannerOpen: boolean;
    isLiteStore: boolean;
    isYCP: boolean;
};

type ResolveTopBarStateParams = ResolveAlertBannerParams &
    ResolvePromoBannerParams & {
        isOnboardingCompleted: boolean;
    };

type TopBarState = {
    layout: TopBarLayout;
    content: TopBarContent;
};

const resolveAlertBanner = ({
    isCompanyNameModerationBannerOpen,
    isCompanyNameModerationLoading,
    isStoreBannedBannerOpen,
}: ResolveAlertBannerParams): TopBarState | null => {
    if (isCompanyNameModerationBannerOpen) {
        return {
            layout: TOP_BAR_LAYOUT.ALERT_COMPACT,
            content: TOP_BAR_CONTENT_TYPES.COMPANY_NAME_MODERATION,
        };
    }

    // Ждём первый ответ модерации названия, чтобы не сменять одну системную плашку другой.
    if (isCompanyNameModerationLoading) {
        return null;
    }

    if (isStoreBannedBannerOpen) {
        return {
            layout: TOP_BAR_LAYOUT.ALERT_EXPANDED,
            content: TOP_BAR_CONTENT_TYPES.STORE_BANNED,
        };
    }

    return null;
};

const resolvePromoBanner = ({
    isCallMeBannerOpen,
    isLiteStore,
    isYCP,
}: ResolvePromoBannerParams): TopBarContent | null => {
    // Промо-баннеры не показываем во внешнем чекауте и Kit Lite.
    if (isYCP || isLiteStore) {
        return null;
    }

    if (isCallMeBannerOpen) {
        return TOP_BAR_CONTENT_TYPES.CALL_ME_BANNER;
    }

    return null;
};

/**
 * Определяет итоговые layout и content для TopBar по приоритетам системных и промо-баннеров.
 *
 * @kind helper
 * @returns Итоговое состояние layout и content для TopBar.
 */
export const resolveTopBarState = (params: ResolveTopBarStateParams): TopBarState => {
    const alertState = resolveAlertBanner(params);

    if (alertState) {
        return alertState;
    }

    if (params.isConfiguredBannerOpen && !params.isCompanyNameModerationLoading) {
        return params.isOnboardingCompleted
            ? {
                  layout: TOP_BAR_LAYOUT.AFTER_ONBOARDING_CONFIGURED,
                  content: TOP_BAR_CONTENT_TYPES.CONFIGURABLE_COMMUNICATION,
              }
            : {
                  layout: TOP_BAR_LAYOUT.ONBOARDING_WITH_CONFIGURED,
                  content: TOP_BAR_CONTENT_TYPES.CONFIGURABLE_COMMUNICATION,
              };
    }

    const promoBanner = resolvePromoBanner(params);

    if (params.isOnboardingCompleted) {
        return promoBanner
            ? { layout: TOP_BAR_LAYOUT.AFTER_ONBOARDING_ADV, content: promoBanner }
            : { layout: TOP_BAR_LAYOUT.HIDDEN, content: TOP_BAR_CONTENT_TYPES.HIDDEN };
    }

    return promoBanner
        ? { layout: TOP_BAR_LAYOUT.ONBOARDING_WITH_ADV, content: promoBanner }
        : { layout: TOP_BAR_LAYOUT.ONLY_ONBOARDING, content: TOP_BAR_CONTENT_TYPES.HIDDEN };
};
